import RolePermissionsModal from "components/RolePermissionsModal";
import type { RoleData } from "components/RolePermissionsModal/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
    fetchRolePermissions,
    fetchRoles,
    fetchSystemRolePermissions,
    updateRolePermissions,
    updateSystemRolePermissions,
} from "store/permissionsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import type { Role } from "../types";

interface EditRoleModalProps {
    open: boolean;
    onClose: () => void;
    role: Role | null;
}

const EditRoleModal = ({ open, onClose, role }: EditRoleModalProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const updateRoleStatus = useAppSelector((state) => state.permissions.updateRoleStatus);
    const [roleData, setRoleData] = useState<RoleData | null>(null);
    const [isPending, setIsPending] = useState(false);

    useEffect(() => {
        if (open && role) {
            setIsPending(true);
            setRoleData(null);

            // Use different endpoint based on role type
            if (role.type === "system") {
                dispatch(fetchSystemRolePermissions(role.originalName))
                    .unwrap()
                    .then((systemRoleData) => {
                        setRoleData({
                            name: role.name,
                            description: systemRoleData.description || role.description || "",
                            permissions: systemRoleData.permissions,
                        });
                        setIsPending(false);
                    })
                    .catch((error: any) => {
                        toast.error(error?.message || (t("Roles.FetchPermissionsError") as string));
                        setIsPending(false);
                        onClose();
                    });
            } else {
                dispatch(fetchRolePermissions(Number(role.id)))
                    .unwrap()
                    .then((permissions) => {
                        setRoleData({
                            name: role.name,
                            description: role.description,
                            permissions: permissions,
                        });
                        setIsPending(false);
                    })
                    .catch((error: any) => {
                        toast.error(error?.message || (t("Roles.FetchPermissionsError") as string));
                        setIsPending(false);
                        onClose();
                    });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, role?.id, role?.originalName, role?.type]);

    const handleSave = async (updatedRoleData: RoleData) => {
        if (!role) return;

        try {
            // Use different endpoint based on role type
            if (role.type === "system") {
                await dispatch(
                    updateSystemRolePermissions({
                        roleName: role.originalName,
                        name: updatedRoleData.name,
                        description: updatedRoleData.description,
                        permissions: updatedRoleData.permissions,
                    }),
                ).unwrap();
            } else {
                await dispatch(
                    updateRolePermissions({
                        roleId: Number(role.id),
                        name: updatedRoleData.name,
                        description: updatedRoleData.description,
                        permissions: updatedRoleData.permissions,
                    }),
                ).unwrap();
            }
            toast.success(t("Roles.RoleUpdatedSuccess") as string);
            onClose();
            dispatch(fetchRoles());
        } catch (error: any) {
            toast.error(error?.message || (t("Roles.RoleUpdatedError") as string));
        }
    };

    const handleClose = () => {
        setRoleData(null);
        setIsPending(false);
        onClose();
    };

    return (
        <RolePermissionsModal
            open={open}
            onClose={handleClose}
            onSave={handleSave}
            mode="edit"
            initialData={roleData || undefined}
            isLoading={updateRoleStatus === "pending"}
            isPending={isPending}
        />
    );
};

export default EditRoleModal;
